/**
 * AttractionMainContent - Attraction variant main content section
 *
 * Renders AttractionItemsTab with inline ticket selection, or description.
 * Switches between attraction sections and description based on selected tab.
 */
import { useTranslation } from "@shared/utils/internationalization";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import type { PageData, AttractionData } from "../../../../adapters/types";
import { DetailPageInfo } from "../../../../components/shared/DetailPageInfo.web";
import { AttractionItemsTab } from "../../../../components/attractions/AttractionItemsTab.web";

interface AttractionMainContentProps {
  data: PageData;
  attraction?: AttractionData;
}

export function AttractionMainContent({
  data,
  attraction,
}: AttractionMainContentProps): JSX.Element {
  const { t } = useTranslation("purchases");
  const { mainContent, stickyTabs, ui, rawData } = data;

  // Build venue info for DetailPageInfo
  const venueInfo = {
    venueId: mainContent.venueInfo.id,
    venueSlug: mainContent.venueInfo.slug ?? "",
    venueName: mainContent.venueInfo.name ?? "",
    venueDescription: mainContent.venueInfo.description,
    venuePrivate: mainContent.venueInfo.isPrivate,
  };

  // Check if we're on the description tab
  const isDescriptionTab =
    attraction && stickyTabs.selectedTab === attraction.descriptionTabId;

  // Show description tab content
  if (isDescriptionTab) {
    return (
      <Flex id="additional-info" scrollMarginTop={String(ui.scrollMargin)}>
        <DetailPageInfo
          infoHeader={t("Description")}
          infoRestrictions={rawData.event?.restrictions}
          infoText={rawData.event?.description ?? ""}
          refundPolicyText={mainContent.refundPolicy ?? ""}
          openContactDetailsModal={data.header.organizer.onContact}
          isLoading={ui.isLoading}
          venueInfo={venueInfo}
          eventId={mainContent.locationInfo?.id}
          accommodationUrl={mainContent.locationInfo?.accommodationUrl}
          isAttractionPage
        />
      </Flex>
    );
  }

  // Show attraction items tab content
  const scrollMarginNum =
    typeof ui.scrollMargin === "number"
      ? ui.scrollMargin
      : parseInt(String(ui.scrollMargin), 10) || 0;

  return (
    <Flex
      id="event-detail-page-tickets"
      scrollMarginTop={String(ui.scrollMargin)}
    >
      <AttractionItemsTab
        basket={attraction?.basket}
        event={rawData.event}
        isBasketUpdating={attraction?.isBasketUpdating ?? false}
        attractionItems={attraction?.config ?? []}
        scrollMargin={scrollMarginNum}
      />
    </Flex>
  );
}
